import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ChatContext } from "../context/ChatContext";

const ConversationItem = ({ conversation, onClick, isWidget }) => {
  const navigate = useNavigate();
  const { deleteConversation } = useContext(ChatContext);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();

    // Reset time parts for date comparison
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const messageDay = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );

    // Format time
    const timeString = date
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();

    // Check if today
    if (messageDay.getTime() === today.getTime()) {
      return timeString;
    }

    // Check if yesterday
    if (messageDay.getTime() === yesterday.getTime()) {
      return `Yesterday, ${timeString}`;
    }

    // Older than yesterday - show full date (shortened for mobile)
    const isMobile = window.innerWidth < 640;
    if (isMobile) {
      // Shorter format for mobile
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: date.getFullYear() !== now.getFullYear() ? "2-digit" : undefined,
      });
    }

    // Full format for desktop
    const dayName = date.toLocaleDateString("en-US", { weekday: "long" });
    const day = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "long" });
    const year = date.getFullYear();

    // Add ordinal suffix to day
    const getOrdinal = (n) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    return `${dayName}, ${getOrdinal(day)} ${month} ${year}`;
  };

  const handleClick = () => {
    if (onClick) {
      onClick(conversation.id);
    } else {
      navigate(`/chat/${conversation.id}`);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this conversation?")) {
      deleteConversation(conversation.id);
    }
  };

  const getLastMessage = () => {
    if (!conversation.messages || conversation.messages.length === 0) {
      return "No messages yet";
    }
    const lastMessage = conversation.messages[conversation.messages.length - 1];
    const preview = lastMessage.content.substring(0, 60);
    return preview + (lastMessage.content.length > 60 ? "..." : "");
  };

  return (
    <div
      onClick={handleClick}
      className="group relative glass border border-blue-500/20 rounded-xl p-3 sm:p-4 cursor-pointer
                 transition-all duration-300 hover:scale-[1.02] hover:border-blue-400/40
                 hover:shadow-[0_0_30px_rgba(56,189,248,0.3)] animate-fadeIn"
    >
      <div className="flex items-start justify-between">
        <div className="flex-grow mr-2 sm:mr-4 min-w-0">
          <h3 className="text-base sm:text-lg font-semibold text-white group-hover:text-blue-300 transition-colors duration-300 truncate">
            {conversation.title}
          </h3>
          <p className="text-xs sm:text-sm text-gray-400 mt-1 line-clamp-2">
            {getLastMessage()}
          </p>
          <p className="text-xs text-gray-500 mt-1.5 sm:mt-2">
            {formatDate(conversation.updatedAt)}
          </p>
        </div>

        <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
          <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-400 rounded-full animate-pulse"></div>

          <button
            onClick={handleDelete}
            className="opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity duration-300
                     p-1.5 sm:p-2 rounded-lg hover:bg-red-500/20 text-red-400 hover:text-red-300"
            title="Delete conversation"
          >
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      </div>

      <div
        className="absolute inset-0 bg-gradient-to-r from-blue-500/0 to-purple-500/0 
                      group-hover:from-blue-500/5 group-hover:to-purple-500/5 
                      rounded-xl transition-all duration-500 pointer-events-none"
      ></div>
    </div>
  );
};

export default ConversationItem;
